import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Eye, 
  MousePointer, 
  Users, 
  Target,
  RefreshCw,
  BarChart3,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const MetricCard = ({ title, value, icon: Icon, change, format = 'number', color = 'primary' }) => {
  const formatValue = (val) => {
    if (format === 'currency') return `$${parseFloat(val).toFixed(2)}`;
    if (format === 'percent') return `${parseFloat(val).toFixed(2)}%`;
    if (format === 'number') return parseInt(val).toLocaleString();
    return val;
  };

  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    blue: 'bg-blue-500/10 text-blue-500',
    green: 'bg-green-500/10 text-green-500',
    yellow: 'bg-yellow-500/10 text-yellow-500',
    purple: 'bg-purple-500/10 text-purple-500',
    red: 'bg-red-500/10 text-red-500'
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{formatValue(value)}</p>
            {change !== undefined && (
              <div className={`flex items-center text-xs mt-1 ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {change >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                {Math.abs(change).toFixed(1)}% vs prev period
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CampaignRow = ({ campaign }) => {
  const statusColors = {
    ACTIVE: 'bg-green-500/20 text-green-400',
    PAUSED: 'bg-yellow-500/20 text-yellow-400',
    ARCHIVED: 'bg-gray-500/20 text-gray-400'
  };

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors">
      <td className="py-3 px-4">
        <div className="font-medium">{campaign.name}</div>
        <div className="text-xs text-muted-foreground">{campaign.objective}</div>
      </td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[campaign.status] || 'bg-gray-500/20'}`}>
          {campaign.status}
        </span>
      </td>
      <td className="py-3 px-4 text-right">{parseInt(campaign.metrics?.impressions || 0).toLocaleString()}</td>
      <td className="py-3 px-4 text-right">{parseInt(campaign.metrics?.clicks || 0).toLocaleString()}</td>
      <td className="py-3 px-4 text-right">{parseFloat(campaign.metrics?.ctr || 0).toFixed(2)}%</td>
      <td className="py-3 px-4 text-right">${parseFloat(campaign.metrics?.spend || 0).toFixed(2)}</td>
      <td className="py-3 px-4 text-right">${parseFloat(campaign.metrics?.cpc || 0).toFixed(2)}</td>
      <td className="py-3 px-4 text-right">{parseInt(campaign.metrics?.conversions || 0).toLocaleString()}</td>
    </tr>
  );
};

const MetaAdsDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);
  const [datePreset, setDatePreset] = useState('last_30d');
  const [accountInfo, setAccountInfo] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);

  const datePresets = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7d', label: 'Last 7 Days' },
    { value: 'last_14d', label: 'Last 14 Days' },
    { value: 'last_30d', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' }
  ];

  useEffect(() => {
    checkConfig();
  }, []);

  useEffect(() => {
    if (isConfigured) {
      fetchData();
    }
  }, [isConfigured, datePreset]);

  const checkConfig = async () => {
    try {
      const { data, error } = await apiFetch('/api/analytics/meta/config');
      if (!error) {
        setIsConfigured(data.configured);
        if (!data.configured) {
          setIsLoading(false);
        }
      } else {
        setError('Failed to check Meta Ads configuration');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Failed to check Meta Ads configuration');
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [accountRes, campaignsRes, insightsRes] = await Promise.all([
        apiFetch('/api/analytics/meta/account'),
        apiFetch(`/api/analytics/meta/campaigns?date_preset=${datePreset}`),
        apiFetch(`/api/analytics/meta/insights?date_preset=${datePreset}`)
      ]);

      if (!accountRes.error) {
        setAccountInfo(accountRes.data);
      }

      if (!campaignsRes.error) {
        setCampaigns(campaignsRes.data?.campaigns || []);
      }

      if (!insightsRes.error) {
        if (insightsRes.data?.data && insightsRes.data.data.length > 0) {
          setInsights(insightsRes.data.data[0]);
        }
      }
    } catch (err) {
      setError('Failed to fetch Meta Ads data. Please check your access token.');
      toast.error('Failed to load Meta Ads data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
    toast.success('Refreshing data...');
  };

  // Calculate totals from campaigns
  const totals = campaigns.reduce((acc, campaign) => ({
    impressions: acc.impressions + (parseInt(campaign.metrics?.impressions) || 0),
    clicks: acc.clicks + (parseInt(campaign.metrics?.clicks) || 0),
    spend: acc.spend + (parseFloat(campaign.metrics?.spend) || 0),
    reach: acc.reach + (parseInt(campaign.metrics?.reach) || 0),
    conversions: acc.conversions + (parseInt(campaign.metrics?.conversions) || 0)
  }), { impressions: 0, clicks: 0, spend: 0, reach: 0, conversions: 0 });

  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
  const avgCpc = totals.clicks > 0 ? (totals.spend / totals.clicks) : 0;

  if (!isConfigured) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Meta Ads Not Configured</h3>
          <p className="text-muted-foreground mb-4">
            Please configure your Meta Ads credentials in the backend environment.
          </p>
          <div className="text-left bg-muted/50 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm font-mono">
              META_ACCESS_TOKEN=your_token<br/>
              FACEBOOK_AD_ACCOUNT_ID=your_account_id
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Meta Ads Dashboard
          </h2>
          {accountInfo && (
            <p className="text-muted-foreground text-sm mt-1">
              Account: {accountInfo.name} • {accountInfo.currency} • {accountInfo.timezone_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {datePresets.map(preset => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="bg-red-500/10 border-red-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3">Loading Meta Ads data...</span>
        </div>
      ) : (
        <>
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <MetricCard
              title="Impressions"
              value={insights?.impressions || totals.impressions}
              icon={Eye}
              color="blue"
            />
            <MetricCard
              title="Reach"
              value={insights?.reach || totals.reach}
              icon={Users}
              color="purple"
            />
            <MetricCard
              title="Clicks"
              value={insights?.clicks || totals.clicks}
              icon={MousePointer}
              color="green"
            />
            <MetricCard
              title="CTR"
              value={insights?.ctr || avgCtr}
              icon={Target}
              format="percent"
              color="yellow"
            />
            <MetricCard
              title="Spend"
              value={insights?.spend || totals.spend}
              icon={DollarSign}
              format="currency"
              color="red"
            />
            <MetricCard
              title="CPC"
              value={insights?.cpc || avgCpc}
              icon={BarChart3}
              format="currency"
              color="primary"
            />
          </div>

          {/* Campaigns Table */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Campaigns ({campaigns.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border text-left text-sm text-muted-foreground">
                        <th className="py-3 px-4">Campaign</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Impressions</th>
                        <th className="py-3 px-4 text-right">Clicks</th>
                        <th className="py-3 px-4 text-right">CTR</th>
                        <th className="py-3 px-4 text-right">Spend</th>
                        <th className="py-3 px-4 text-right">CPC</th>
                        <th className="py-3 px-4 text-right">Conversions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map(campaign => (
                        <CampaignRow key={campaign.id} campaign={campaign} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No campaigns found for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MetaAdsDashboard;
